import React from "react";
import axios from "axios";
import { get } from "lodash";
import { toast } from "react-toastify";
import { withRouter } from "react-router-dom";
import { FieldSet, KeyValue, Spinner } from "../elements";
import { Documents } from "../components";
import { API_URL } from "../../lib/const";
import ComponentWithToken from "../components/CompontentWithToken";
import DownloadButton from "../components/DownloadButton";
import { PixStatusIndicator } from "../components/PixPaymentSummary";
import {
  fetchFinanceDataSummary,
  generateOverdueBoleto,
} from "../services/finance_data_service";
import ListBoleto from "../components/boleto/ListBoleto";
import {
  BOLETO_SERVICE,
  CONTRACT_STATUS,
  ENROLLMENT_KIND,
  ENROLLMENT_STATUS,
  PAYMENT_METHOD,
  PAYMENT_STATUS,
} from "../../lib/config";
import Button from "../v2/common/components/Button";
import { QRCodeSVG } from "qrcode.react";

const PIX_ERROR_MESSAGES = {
  ERROR: 'Não foi possível concluir o pagamento PIX. Entre em contato com a instituição para regularizar o pagamento.',
  VALOR_DIVERGENTE: 'Identificamos um pagamento PIX com valor diferente do esperado. Entre em contato com a instituição para regularizar o pagamento.',
};

const getFirstReadableMessage = (payload) => {
  if (!payload) return null;

  if (typeof payload === 'string') {
    const message = payload.trim();
    return message && !message.startsWith('<') ? message : null;
  }

  if (Array.isArray(payload)) {
    return payload
      .map(getFirstReadableMessage)
      .find(Boolean) || null;
  }

  if (typeof payload === 'object') {
    const preferredKeys = [
      'detail',
      'message',
      'non_field_errors',
      'errors',
      'error',
      'messages',
    ];
    const preferredMessage = preferredKeys
      .map((key) => getFirstReadableMessage(payload[key]))
      .find(Boolean);

    if (preferredMessage) return preferredMessage;

    return Object.values(payload)
      .map(getFirstReadableMessage)
      .find(Boolean) || null;
  }

  return null;
};

export const getPixErrorMessage = (pixData) => (
  PIX_ERROR_MESSAGES[get(pixData, 'status')] || null
);

export const getPixRequestErrorMessage = (error, fallbackMessage) => (
  getFirstReadableMessage(get(error, 'response.data')) || fallbackMessage
);

export const getMostCompletePixData = (...pixDataOptions) => (
  pixDataOptions.find((pixData) => get(pixData, 'pix_copia_e_cola')) ||
  pixDataOptions.find(Boolean) ||
  null
);

export const canSendProofOfPayment = ({
  processStatus,
  paymentStatus,
  ProofSent,
  proofsOfPayment,
}) => (
  (get(paymentStatus, 'id') === PAYMENT_STATUS.WAITING_PAYMENT ||
  get(paymentStatus, 'id') === PAYMENT_STATUS.PAYMENT_CONFIRMED) &&
  !ProofSent &&
  get(proofsOfPayment, 'length', 0) > 0
);

export const canShowPixViewer = ({
  enrollmentKind,
  processStatus,
  financeData,
  bbPixConfig,
}) => (
  Boolean(bbPixConfig) &&
  bbPixConfig.enable_pix_copia_e_cola !== false &&
  Boolean(get(financeData, 'plan')) &&
  get(financeData, 'boleto_service.id') === BOLETO_SERVICE.BB &&
  (
    enrollmentKind !== ENROLLMENT_KIND.ENROLLMENT_RENEW ||
    [
      ENROLLMENT_STATUS.WAITING_PAYMENT,
      ENROLLMENT_STATUS.PAYMENT_REVIEWING,
      ENROLLMENT_STATUS.FINALIZED,
    ].includes(get(processStatus, 'id'))
  )
);

const PixViewer = ({
  enrollmentKind,
  processStatus,
  financeData,
  bbPixConfig,
  pixData,
  onGenerate,
  onRegenerate,
  pixLoading,
  pixRegenerating,
  pixCopied,
  onCopy,
}) => {
  if (!canShowPixViewer({
    enrollmentKind,
    processStatus,
    financeData,
    bbPixConfig,
  })) return <></>;

  const pixErrorMessage = getPixErrorMessage(pixData);

  return (
    <>
      <FieldSet title="PIX Copia e Cola" />
      <div className="mb-5 mt-5">
        {!pixData ? (
          <Button onClick={onGenerate} disabled={pixLoading} loading={pixLoading}>
            Gerar PIX Copia e Cola
          </Button>
        ) : (
          <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
            <p className="pix-viewer__instruction">
              Aponte a câmera do seu celular para o QR Code abaixo para efetuar o pagamento
            </p>
            {pixData.status && (
              <p className="pix-viewer__instruction">
                Status do PIX: <PixStatusIndicator status={pixData.status} />
              </p>
            )}
            {pixErrorMessage && (
              <p className="pix-viewer__instruction">
                {pixErrorMessage}
              </p>
            )}
            <div className="pix-viewer__qrcode-card">
              <QRCodeSVG value={pixData.pix_copia_e_cola} size={180} />
            </div>
            <p className="pix-viewer__instruction">
              Ou copie o código abaixo e cole no seu app de pagamentos
            </p>
            <textarea
              readOnly
              className="form-control mb-3 pix-viewer__textarea"
              rows={4}
              value={pixData.pix_copia_e_cola}
            />
            <Button onClick={onCopy}>
              {pixCopied ? 'Copiado!' : 'Copiar código'}
            </Button>
            {pixData.can_regenerate_pix && (
              <div className="mt-3">
                <Button
                  disabled={pixRegenerating}
                  loading={pixRegenerating}
                  onClick={onRegenerate}
                >
                  Gerar novo PIX
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const ContractViewer = ({ token, contractStatus }) => {
  if (contractStatus.name === 'Aguardando geração') return <></>;

  return (
    <>
      <FieldSet title="Contrato" />
      <DownloadButton
        href={`${API_URL}/contracts/enrollment/${token}/contract`}
        label="Baixar contrato"
      />
      {contractStatus.id === CONTRACT_STATUS.WAITING_SIGNS && (
        <div className="row mb-3 mt-3">
          <div className="col-sm-12 mt-3">
            <label>
              Para assinar o contrato digitalmente busque no seu provedor de
              e-mail pelo contrato enviado pela CertOne.
            </label>
          </div>
        </div>
      )}
    </>
  );
}

const PaymentViewer = ({
  proofs_of_payment,
  enrollmentKind,
  processStatus,
  financeData,
  complementaryBoletos,
  handleDocumentUpload,
  handleProofOfPaymentRemove,
  paymentStatus = { name: 'Boleto não emitido' },
  ProofSent,
  bbPixConfig,
  pixData,
  onPixGenerate,
  onPixRegenerate,
  pixLoading,
  pixRegenerating,
  pixCopied,
  onPixCopy,
}) => {
  if (get(paymentStatus, 'id') === PAYMENT_STATUS.EXEMPT) return <></>;

  return (
    <>
      <FieldSet title="Financeiro" />
      <div className="enrollment-flex">
        <KeyValue label="Plano" title={get(financeData, 'plan.name')} />
        <KeyValue label="Método de Pagamento" title={get(financeData, 'payment_method.name')} />
        <KeyValue title={get(financeData, 'plan.value')} label="Total" />
        <KeyValue title={get(financeData, 'plan.installments')} label="Parcelas" />
        <KeyValue title={get(financeData, 'plan.installment_value')} label="Valor da Parcela" />
        <KeyValue title={get(financeData, 'plan.due_date')} label="Vencimentos" />
        {get(financeData, 'guarantee_type') && (<KeyValue title={get(financeData, 'guarantee_type.name')} label="Tipo de garantia" />)}
      </div>
      {financeData && financeData.boleto_id && (
        <>
          <div className="ml-3">
            <ListBoleto
              boleto={financeData.boleto_id}
              complementaryBoletos={complementaryBoletos}
            />
            {financeData.payment_method.id === PAYMENT_METHOD.INSTITUITION_BOLETO && !ProofSent && (
              <div
                className="documents-base row"
                style={{ justifyContent: "flex-start" }}
              >
                <Documents
                  id="Comprovante de pagamento"
                  label="Comprovante de pagamento"
                  onUpload={handleDocumentUpload}
                  onRemove={handleProofOfPaymentRemove}
                  documents={proofs_of_payment}
                />
              </div>
            )}
          </div>
        </>
      )}
      <PixViewer
        enrollmentKind={enrollmentKind}
        processStatus={processStatus}
        financeData={financeData}
        bbPixConfig={bbPixConfig}
        pixData={pixData}
        onGenerate={onPixGenerate}
        onRegenerate={onPixRegenerate}
        pixLoading={pixLoading}
        pixRegenerating={pixRegenerating}
        pixCopied={pixCopied}
        onCopy={onPixCopy}
      />
    </>
  );
}

const ProcessViewer = ({ processStatus, documentStatus, contractStatus, paymentStatus }) => {
  return (
    <>
      <FieldSet title="Processo" />
      <div className="enrollment-flex">
        <KeyValue label="Situação" title={processStatus.name} />
        <KeyValue label="Situação da Documentação" title={documentStatus.name} />
        <KeyValue label="Situação do Pagamento" title={paymentStatus.name} />
        <KeyValue label="Situação do Contrato" title={contractStatus.name} />
      </div>
    </>
  );
}

class Payment extends ComponentWithToken {
  state = {
    loading: false,
    invoices: [],
    proofs_of_payment: [],
    ProofSent: true,
    complementaryBoletos: [],
    canUpdateExpiredBill: null,
    processStatus: { name: 'Preenchendo' },
    contractStatus: { name: 'Aguardando geração' },
    paymentStatus: { name: 'Boleto não emitido' },
    documentStatus: { name: 'Não preenchido' },
    enrollmentKind: null,

    financeData: {},
    bbPixConfig: null,
    pixData: null,
    pixLoading: false,
    pixRegenerating: false,
    pixCopied: false,
  };

  componentDidMount() {
    Promise.all([
      this.fetchEnrollment(),
      this.fetchFinanceData(),
      this.fetComplementaryBoletos(),
      this.fetchBBPixConfig().then((bbPixConfig) => {
        if (bbPixConfig.enable_pix_copia_e_cola === false) return null;
        return this.fetchPixData();
      }),
    ]).finally(() => this.setState({ loading: false }));
  }

  fetchBBPixConfig = () =>
    axios
      .get(`${API_URL}/finance/bb-pix-config/`)
      .then((response) => {
        this.setState({ bbPixConfig: response.data });
        return response.data;
      })
      .catch(() => {
        const bbPixConfig = {
          enable_pix_copia_e_cola: true,
          enable_pix_qrcode_on_boleto: true,
        };
        this.setState({ bbPixConfig });
        return bbPixConfig;
      });

  fetComplementaryBoletos = () =>
    axios
      .get(`${API_URL}/onboarding/complementary-boletos/${this.token}`)
      .then((response) =>
        this.setState({ complementaryBoletos: response.data })
      );

  async fetchEnrollment() {
    this.setState({ loading: true });
    const response = await axios.get(`${API_URL}/onboarding/enroll/${this.token}`);

    this.setState({
      envelopeStatus: get(response, "data.envelope_status"),
      ProofSent: get(response, "data.proof_sent"),
      processStatus: get(response, "data.status") || { name: 'Preenchendo' },
      contractStatus: get(response, "data.contract_status") || { name: 'Aguardando geração' },
      paymentStatus: get(response, "data.payment_status") || { name: 'Boleto não emitido' },
      documentStatus: get(response, "data.document_status") || { name: 'Não preenchido' },
      enrollmentKind: get(response, "data.kind"),
    });
  }

  fetchFinanceData = () =>
    fetchFinanceDataSummary(this.token).then((response) =>
      this.setState({ financeData: response }))

  fetchPixData = (shouldUpdateState = true) =>
    axios
      .get(`${API_URL}/finance/pix/${this.token}`)
      .then((response) => {
        if (shouldUpdateState) {
          this.setState({ pixData: response.data });
        }
        return response.data;
      })
      .catch(() => null);

  handleDocumentUpload = (event) => {
    const file = get(event, "target.files.0");
    const data = new FormData();
    data.append("file", file);
    data.append("document_type", 6);
    return axios
      .post(`${API_URL}/uploads/`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        this.setState((prevState) => {
          const { proofs_of_payment } = prevState;
          proofs_of_payment.push({ key: response.data.key, name: file.name });
          return { proofs_of_payment };
        });
      });
  };

  handleProofOfPaymentRemove = (key) => {
    const { proofs_of_payment } = this.state;
    const new_proofs_of_payment = proofs_of_payment.filter(
      (proof) => proof.key !== key
    );
    this.setState({ proofs_of_payment: new_proofs_of_payment });
  };

  handleSend() {
    const { proofs_of_payment } = this.state;
    const { token } = this.props;
    this.setState({ loading: true });
    const data = {
      proofs_of_payment,
    };

    axios
      .post(`${API_URL}/onboarding/proof-of-payment/${token}`, data)
      .then(() => {
        toast.success("Comprovante de pagamento enviado", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        this.componentDidMount();
      })
      .catch((error) => {
        toast.error(
          get(error, "response.data[0]", "Ocorreu um erro inesperado"),
          { position: toast.POSITION.BOTTOM_RIGHT }
        );
        this.setState({
          loading: false,
        });
      });
  }

  generateOverdueBoleto = () => {
    generateOverdueBoleto(this.token).then((d) => {
      this.setState({ submittingBoleto: false, boleto_id: d.boleto_id });
      toast.success("Boleto gerado com sucesso!");
    });
  };

  handleGeneratePix = () => {
    this.setState({ pixLoading: true });
    axios
      .post(`${API_URL}/finance/pix/${this.token}`)
      .then((response) => {
        this.setState({
          pixData: response.data,
          pixLoading: false,
        });
        this.fetchPixData(false).then((pixData) => {
          this.setState({
            pixData: getMostCompletePixData(response.data, pixData),
          });
        });
      })
      .catch((error) => {
        toast.error(getPixRequestErrorMessage(error, 'Erro ao gerar PIX'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        this.setState({ pixLoading: false });
      });
  };

  handleRegeneratePix = () => {
    this.setState({ pixRegenerating: true });
    axios
      .post(`${API_URL}/finance/pix/${this.token}/regenerate`)
      .then((response) => {
        this.setState({
          pixData: response.data,
          pixRegenerating: false,
        });
        toast.success('Novo PIX gerado com sucesso', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        this.fetchPixData(false).then((pixData) => {
          this.setState({
            pixData: getMostCompletePixData(response.data, pixData),
          });
        });
      })
      .catch((error) => {
        toast.error(getPixRequestErrorMessage(error, 'Erro ao gerar novo PIX'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        this.setState({ pixRegenerating: false });
      });
  };

  handleCopyPix = () => {
    const pixCode = get(this.state, 'pixData.pix_copia_e_cola');
    if (!pixCode) return;

    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      toast.error('Erro ao copiar código PIX', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    navigator.clipboard.writeText(pixCode)
      .then(() => {
        this.setState({ pixCopied: true });
        setTimeout(() => this.setState({ pixCopied: false }), 2000);
      })
      .catch(() => {
        toast.error('Erro ao copiar código PIX', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };

  render() {
    const {
      loading,
      proofs_of_payment,
      complementaryBoletos,
      ProofSent,
      processStatus,
      contractStatus,
      paymentStatus,
      documentStatus,
      enrollmentKind,
      financeData,
      bbPixConfig,
      pixData,
      pixLoading,
      pixRegenerating,
      pixCopied,
    } = this.state;
    const { backClick } = this.props;
    return (
      <Spinner active={loading}>
        <ProcessViewer processStatus={processStatus} documentStatus={documentStatus} contractStatus={contractStatus} paymentStatus={paymentStatus} />
        <ContractViewer token={this.token} contractStatus={contractStatus} />
        <PaymentViewer
          proofs_of_payment={proofs_of_payment}
          complementaryBoletos={complementaryBoletos}
          handleDocumentUpload={this.handleDocumentUpload}
          handleProofOfPaymentRemove={this.handleProofOfPaymentRemove}
          enrollmentKind={enrollmentKind}
          processStatus={processStatus}
          financeData={financeData}
          bbPixConfig={bbPixConfig}
          paymentStatus={paymentStatus}
          ProofSent={ProofSent}
          pixData={pixData}
          onPixGenerate={this.handleGeneratePix}
          onPixRegenerate={this.handleRegeneratePix}
          pixLoading={pixLoading}
          pixRegenerating={pixRegenerating}
          pixCopied={pixCopied}
          onPixCopy={this.handleCopyPix}
        />
        <div className="text-right">
          <div className="col-md-4 p-1 offset-md-8 col-sm-12">
            <div className="btn-actions">
              {get(financeData, 'plan.update_expired_bill') && financeData.bill_is_expired && (
                <Button
                  disabled={!financeData.bill_is_expired}
                  onClick={() => this.generateOverdueBoleto()}
                >
                  Gerar novo boleto
                </Button>
              )}
              <button
                className="btn btn-black mr-2"
                type="button"
                onClick={() => backClick()}
              >
                Voltar
              </button>
              {(get(paymentStatus, 'id') === PAYMENT_STATUS.WAITING_PAYMENT ||
                get(paymentStatus, 'id') === PAYMENT_STATUS.PAYMENT_CONFIRMED) && !ProofSent && (
                <Button
                  disabled={!canSendProofOfPayment({
                    processStatus,
                    paymentStatus,
                    ProofSent,
                    proofsOfPayment: proofs_of_payment,
                  })}
                  onClick={() => this.handleSend()}
                >
                  Enviar
                </Button>
              )}
            </div>
          </div>
        </div>
      </Spinner>
    );
  }
}

export default withRouter(Payment);
